import type { DeviceDetail, DeviceStatus } from './types'
import { bytesToHexString } from './protocol'

// Record 约束保证 DeviceStatus 新增字段后必须补上中文日志说明。
export const DEVICE_STATUS_LABELS: Record<keyof DeviceStatus, string> = {
  connectionType: '当前连接方式',
  isDongleConnection: '当前是否为2.4G连接',
  headsetConnected: '耳机连接dongle状态',
  micByte: '当前链路麦克风原始字节',
  micMute: '当前链路麦克风静音',
  micVolumeLevel: '当前链路麦克风音量',
  usbMicByte: '耳机USB麦克风原始字节',
  usbMicMute: '耳机USB麦克风静音',
  usbMicVolumeLevel: '耳机USB麦克风音量',
  dongleMicByte: 'dongle麦克风原始字节',
  dongleMicMute: 'dongle麦克风静音',
  dongleMicVolumeLevel: 'dongle麦克风音量',
  mixVolume: '混音比例',
  splitVolume: '分离音比例',
  adaptiveVolume: '自适应音比例',
  speakerByte: '当前链路喇叭原始字节',
  speakerMute: '当前链路喇叭静音',
  speakerVolumeLevel: '当前链路喇叭音量',
  usbSpeakerByte: '耳机USB喇叭原始字节',
  usbSpeakerMute: '耳机USB喇叭静音',
  usbSpeakerVolumeLevel: '耳机USB喇叭音量',
  dongleSpeakerByte: 'dongle喇叭原始字节',
  dongleSpeakerMute: 'dongle喇叭静音',
  dongleSpeakerVolumeLevel: 'dongle喇叭音量',
  callBridgeByte: '多方通话总开关及通路位',
  callBridgeEnabled: '多方通话总开关',
  callBridgeChannelMask: '多方通话通路位掩码',
  callBridgeBtEnabled: '多方通话BT通路开关',
  callBridgeDongleEnabled: '多方通话2.4G通路开关',
  callBridgeUsbEnabled: '多方通话USB通路开关',
  chickenByte: '当前链路一键吃鸡原始字节',
  chickenOneKey: '当前链路一键吃鸡开关',
  chickenTag: '当前链路一键吃鸡标签',
  usbChickenByte: '耳机USB一键吃鸡原始字节',
  usbChickenOneKey: '耳机USB一键吃鸡开关',
  usbChickenTag: '耳机USB一键吃鸡标签',
  dongleChickenByte: 'dongle一键吃鸡原始字节',
  dongleChickenOneKey: 'dongle一键吃鸡开关',
  dongleChickenTag: 'dongle一键吃鸡标签',
  voiceByte: '变声原始字节',
  voiceChangeEnabled: '变声开关',
  voiceChangeMode: '变声模式',
  sidetoneByte: '耳返原始字节',
  sidetoneEnabled: '耳返开关',
  sidetoneVolume: '耳返音量',
  btShareEnabled: '蓝牙分享开关',
  noiseByte: '降噪原始字节',
  noiseReductionEnabled: '降噪开关',
  noiseReductionMode: '降噪档位原始值',
  ledMode: 'LED灯模式',
  eqTagByte: '当前链路EQ标签原始值',
  eqControlByte: '当前链路EQ控制字节',
  eqByte: '当前链路EQ控制字节兼容字段',
  eqEnabled: '当前链路EQ开关',
  eqMode: '当前链路EQ模式',
  eqTag: '当前链路EQ标签',
  usbEqTag: '耳机USBEQ标签',
  usbEqControlByte: '耳机USBEQ控制字节',
  usbEqEnabled: '耳机USBEQ开关',
  usbEqMode: '耳机USBEQ模式',
  dongleEqTag: 'dongleEQ标签',
  dongleEqControlByte: 'dongleEQ控制字节',
  dongleEqEnabled: 'dongleEQ开关',
  dongleEqMode: 'dongleEQ模式',
  linkStatus: '耳机USB连接状态',
  batteryLevel: '耳机电量百分比',
  powerOffStatus: '耳机软件关机状态',
  headsetColor: '耳机颜色原始值',
  headsetColorName: '耳机颜色名称',
  batteryCharging: '耳机充电状态（0未充电1充电中2已充满）',
  bluetoothStatusByte: '蓝牙状态原始字节',
  bluetoothConnectionMark: '蓝牙是否连接兼容字段',
  bluetoothConnected: '蓝牙连接状态',
  bluetoothInCall: '蓝牙通话状态',
  bluetoothPlaying: '蓝牙播放状态',
  productType: '产品类型（0耳机1麦克风）',
  encryptData: '加密数据B20至B27',
  dongleVersion: 'dongle软件版本',
  softwareVersion: '耳机软件版本',
  bluetoothVersion: '蓝牙软件版本',
  noiseCancelVersion: '降噪软件版本',
  audioChannelMode: '音频模式（0Mix1Split2Adaptive）',
  audioStreamByte: '音频流原始字节',
  dongleAudioPlaying: 'dongle音频播放状态',
  headsetAudioPlaying: '耳机音频播放状态',
  hardwareMuteByte: '硬件静音原始字节',
  hardwareMuted: '硬件静音状态',
  scenarioValue: '场景应用原始值B46',
  reserved47to53: '预留数据B47至B53',
  reserved46to53: '旧预留字段B46至B53（含场景值）',
  rawPayloadBytes: '原始状态数据B0至B53',
}

const DEVICE_DETAIL_LABELS: Record<keyof DeviceDetail, string> = {
  deviceCategory: '设备分类', vendorId: '厂商VID', productId: '产品PID',
  deviceName: '设备名称', deviceSerial: '设备序列号', firmwareVersion: '固件版本',
  hardwareVersion: '硬件版本', protocolVersion: '协议版本', deviceTypeCode: '设备类型代码',
  softwareVersion: '耳机软件版本', bluetoothVersion: '蓝牙软件版本',
  noiseCancelVersion: '降噪软件版本', dongleVersion: 'dongle软件版本',
  color: '设备颜色原始值', colorName: '设备颜色名称',
}

function formatFields<T extends object>(data: T, labels: Record<keyof T, string>): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const [field, label] of Object.entries(labels) as Array<[keyof T, string]>) {
    const value = data[field]
    // 创建独立快照，避免 console 延迟展开时看到后续被 UI 修改过的数据。
    result[`${String(field)}-${label}`] = Array.isArray(value)
      ? bytesToHexString(value)
      : value ?? '未上报'
  }
  return result
}

export function formatDeviceStatusForLog(status: DeviceStatus): Record<string, unknown> {
  return formatFields(status, DEVICE_STATUS_LABELS)
}

export function formatDeviceDetailForLog(detail: DeviceDetail): Record<string, unknown> {
  return formatFields(detail, DEVICE_DETAIL_LABELS)
}
